import React, { useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MyContext } from "../../../src/App";
import googleIcon from "../../assets/images/googlereg.png";
import { postData } from "../../utils/api";
import CircularProgress from "@mui/material/CircularProgress";
import firebase from "../../firebase";
import { getAuth, signInWithPopup, GoogleAuthProvider } from "firebase/auth";

const provider = new GoogleAuthProvider();
provider.setCustomParameters({
  prompt: "select_account", 
});
const auth = getAuth();

const RegisterForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formFields, setFormFields] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  const navigate = useNavigate();
  const context = useContext(MyContext);

  const handleChange = (e) => {
    setFormFields({ ...formFields, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formFields.name.trim()) {
      context.setAlertBox({ open: true, error: true, msg: "Full Name is required" });
      return;
    }

    // Email Regex matching backend
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!formFields.email.trim() || !emailRegex.test(formFields.email)) {
      context.setAlertBox({ open: true, error: true, msg: "Please enter a valid email address (e.g., name@example.com)" });
      return;
    }

    // Phone validation (10 digit Indian number)
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!formFields.phone.trim() || !phoneRegex.test(formFields.phone)) {
      context.setAlertBox({ open: true, error: true, msg: "Please enter a valid 10-digit Indian mobile number" });
      return;
    }

    // Password validation matching backend
    const password = formFields.password;
    if (!password || password.length < 7) {
      context.setAlertBox({ open: true, error: true, msg: "Password must be at least 7 characters long" });
      return;
    }
    if (!/[A-Za-z]/.test(password)) {
      context.setAlertBox({ open: true, error: true, msg: "Password must contain at least one letter" });
      return;
    }
    if (!/\d/.test(password)) {
      context.setAlertBox({ open: true, error: true, msg: "Password must contain at least one number" });
      return;
    }
    if (!/[@$!%*?&]/.test(password)) {
      context.setAlertBox({ open: true, error: true, msg: "Password must contain at least one special character (@$!%*?&)" });
      return;
    }

    if (formFields.confirmPassword !== formFields.password) {
      context.setAlertBox({ open: true, error: true, msg: "Passwords do not match" });
      return;
    }

    context.setIsLoading(true);

    const userData = {
      name: formFields.name,
      email: formFields.email,
      phone: formFields.phone,
      password: formFields.password,
    };

    try {

      const res = await postData("/api/users/signup", userData);
      

      
      if (res && !res.error) {
        // Store token and user data
        localStorage.setItem("token", res.token);
        localStorage.setItem(
          "user",
          JSON.stringify({
            name: res.user?.name,
            email: res.user?.email,
            userId: res.user?.id,
          })
        );

        context.setIsLoging(true);

        // Get cart data if function exists
        if (context.getCartData) {
          context.getCartData();
        }

        context.setAlertBox({
          open: true,
          error: false,
          msg: res.msg || "Registration successful! Welcome!",
        });
        
        setTimeout(() => {
          navigate("/");
        }, 1500);
        
      } else {
        context.setAlertBox({
          open: true,
          error: true,
          msg: res?.msg || "Registration failed. Please try again.",
        });
      }
    } catch (err) {
      console.error("Registration error:", err);
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Network error. Please check your connection and try again.",
      });
    } finally {
      context.setIsLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    context.setIsLoading(true);
    
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const fields = {
        name: user.displayName,
        email: user.email,
        password: "",
        images: user.photoURL,
        isAdmin: false,
      };

      if (user.phoneNumber && user.phoneNumber.trim() !== "") {
        fields.phone = user.phoneNumber;
      }

      const res = await postData("/api/users/authWithGoogle", fields);
      
      if (res && !res.error) {
        localStorage.setItem("token", res.token);
        localStorage.setItem(
          "user",
          JSON.stringify({
            name: res.user?.name,
            email: res.user?.email,
            userId: res.user?.id,
          })
        );

        context.setIsLoging(true);

        if (context.getCartData) {
          context.getCartData();
        }

        context.setAlertBox({ open: true, error: false, msg: res.msg });
        
        setTimeout(() => {
          navigate("/");
        }, 2000);
      } else {
        context.setAlertBox({ 
          open: true, 
          error: true, 
          msg: res?.msg || "Google authentication failed" 
        });
      }
    } catch (err) {
      console.error("Google Sign-in Error:", err);
      
      if (err.code === "auth/cancelled-popup-request") {
        context.setAlertBox({
          open: true,
          error: true,
          msg: "Sign-in was cancelled",
        });
      } else if (err.code === "auth/popup-blocked") {
        context.setAlertBox({
          open: true,
          error: true,
          msg: "Popup blocked. Please allow popups for this site.",
        });
      } else {
        context.setAlertBox({
          open: true,
          error: true,
          msg: "Google sign-in failed. Please try again.",
        });
      }
    } finally {
      context.setIsLoading(false);
    }
  };

  return (
    <div className="container d-flex justify-content-center align-items-center vh-100">
      <div className="col-md-6 col-lg-5 shadow p-4 rounded bg-white">
        <h3 className="text-center mb-4">Create Account</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-floating mb-3">
            <input
              type="text"
              className="form-control"
              placeholder="Full Name"
              name="name"
              value={formFields.name}
              onChange={handleChange}
              required
            />
            <label>Full Name</label>
          </div>

          <div className="form-floating mb-3">
            <input
              type="email"
              className="form-control"
              placeholder="Email Address"
              name="email"
              value={formFields.email}
              onChange={handleChange}
              required
            />
            <label>Email Address</label>
          </div>

          <div className="form-floating mb-3">
            <input
              type="tel"
              className="form-control"
              placeholder="Phone Number"
              name="phone"
              value={formFields.phone}
              onChange={handleChange}
              required
            />
            <label>Phone Number</label>
          </div>

          <div className="form-floating mb-3 position-relative">
            <input
              type={showPassword ? "text" : "password"}
              className="form-control"
              placeholder="Password"
              name="password"
              value={formFields.password}
              onChange={handleChange}
              minLength="6"
              required
            />
            <label>Password (min 6 characters)</label>
            <span
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: "absolute",
                right: "15px",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
                color: "#555",
              }}
            >
              {showPassword ? "Hide" : "Show"}
            </span>
          </div>

          <div className="form-floating mb-3 position-relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              className="form-control"
              placeholder="Confirm Password"
              name="confirmPassword"
              value={formFields.confirmPassword}
              onChange={handleChange}
              required
            />
            <label>Confirm Password</label>
            <span
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              style={{
                position: "absolute",
                right: "15px",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
                color: "#555",
              }}
            >
              {showConfirmPassword ? "Hide" : "Show"}
            </span>
          </div>

          <button type="submit" className="btn btn-success w-100 mt-4" disabled={context.isLoading}>
            {context.isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Registration"
            )}
          </button>

          <p className="mt-3 text-center">
            Already have an account? <Link to="/login">Login</Link>
          </p>

          <div className="text-center mt-3">
            <p>Or</p>
            <button
              type="button"
              className="btn btn-outline-light border w-100 d-flex align-items-center justify-content-center"
              onClick={signInWithGoogle}
              style={{ gap: "10px" }}
              disabled={context.isLoading}
            >
              <img src={googleIcon} alt="Google" style={{ height: "20px" }} />
              <span style={{ color: "black" }}>Continue with Google</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterForm;
